import React, { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { NotificationContainer } from "react-notifications";
import "react-notifications/lib/notifications.css";

import SiteLayout from "./Components/layout/SiteLayout";
import HomePage from "./Pages/HomePage";
import NotFoundPage from "./Pages/NotFoundPage";

// Everything except the homepage is split out of the initial bundle. The
// homepage is what a first-time visitor and a crawler land on, so it ships
// eagerly; the rest arrives on navigation. The admin area in particular has no
// business being in the bundle a homeowner downloads.
const ServicesPage = lazy(() => import("./Pages/ServicesPage"));
const ServiceDetailPage = lazy(() => import("./Pages/ServiceDetailPage"));
const ProjectsPage = lazy(() => import("./Pages/ProjectsPage"));
const AboutPage = lazy(() => import("./Pages/AboutPage"));
const ContactPage = lazy(() => import("./Pages/ContactPage"));
const ServiceAreasPage = lazy(() => import("./Pages/ServiceAreasPage"));
const LocationPage = lazy(() => import("./Pages/LocationPage"));
const JournalPage = lazy(() => import("./Pages/JournalPage"));
const JournalPostPage = lazy(() => import("./Pages/JournalPostPage"));

const Login = lazy(() => import("./Components/Login/Login"));
const Signup = lazy(() => import("./Components/Signup/Signup"));
const Forgot = lazy(() => import("./Forgot/Forgot"));
const AdminDashboardPage = lazy(() => import("./Pages/AdminDashboardPage"));
const ProtectedRoute = lazy(() => import("./Components/ProtectedRoute"));

// The four city pages are one component driven by data/locations.js, so the
// routes are generated rather than written out four times.
const CITY_SLUGS = ["pickering", "ajax", "whitby", "durham-region"];

/**
 * Deliberately blank. A spinner that flashes for 80ms on a local chunk load is
 * more distracting than nothing, and the header is already painted.
 */
const RouteFallback = () => <div style={{ minHeight: "60vh" }} />;

const withLayout = (element) => <SiteLayout>{element}</SiteLayout>;

function App() {
  return (
    <BrowserRouter>
      {/* Used by the admin area only; the public site reports status inline. */}
      <NotificationContainer />

      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* ── Public ─────────────────────────────────────────────── */}
          <Route path="/" element={withLayout(<HomePage />)} />
          <Route path="/services" element={withLayout(<ServicesPage />)} />
          <Route path="/services/:serviceName" element={withLayout(<ServiceDetailPage />)} />
          <Route path="/projects" element={withLayout(<ProjectsPage />)} />
          <Route path="/about" element={withLayout(<AboutPage />)} />
          <Route path="/contact" element={withLayout(<ContactPage />)} />
          <Route path="/service-areas" element={withLayout(<ServiceAreasPage />)} />
          <Route path="/blogs" element={withLayout(<JournalPage />)} />
          <Route path="/blogs/:blogId" element={withLayout(<JournalPostPage />)} />

          {/* Literal paths, not `/:citySlug` — a wildcard here would swallow
              every unmatched one-segment URL and turn the 404 into a redirect.
              The slug is handed to the page directly. */}
          {CITY_SLUGS.map((slug) => (
            <Route
              key={slug}
              path={`/${slug}`}
              element={withLayout(<LocationPage slug={slug} />)}
            />
          ))}

          {/* ── Redirects from the previous URL structure ───────────
              /portfolio and /aboutus are in the live sitemap and may be
              indexed or bookmarked. `replace` keeps the old URL out of the
              history stack so Back doesn't bounce between the two.
              These are client-side only — a server-side 301 in
              public/.htaccess would pass more link equity and is worth adding
              at the host if these ever mattered for ranking. */}
          <Route path="/portfolio" element={<Navigate to="/projects" replace />} />
          <Route path="/aboutus" element={<Navigate to="/about" replace />} />

          {/* ── Admin ──────────────────────────────────────────────
              Outside SiteLayout: no public header, footer or nav. */}
          <Route path="/admin/login" element={<Login />} />
          <Route path="/admin/signup" element={<Signup />} />
          <Route path="/forgot" element={<Forgot />} />
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute>
                <AdminDashboardPage />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={withLayout(<NotFoundPage />)} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
